// src/store/modules/theme.js
import axios from "@/config/axios";
import Vue from "vue";
// No import for applyColors

const state = {
  currentTheme: null,
  availableThemes: [],
  theme_palatte: null,
  themeType: "",
  themeMode: "",
};

const mutations = {
  SET_CURRENT_THEME(state, theme) {
    state.currentTheme = theme;
  },
  SET_AVAILABLE_THEMES(state, themes) {
    state.availableThemes = themes;
  },
  SET_THEME_PALATTE(state, palette) {
    state.theme_palatte = palette;
  },
  SET_CURRENT_THEME_TYPE(state, themeType) {
    state.themeType = themeType;
  },
  SET_CURRENT_THEME_MODE(state, themeMode) {
    state.themeMode = themeMode;
  },
};

const actions = {
  async updateTheme({ commit, rootState, state, dispatch }, theme) {
    try {
      const companyId = rootState.company.companyDetails?._id;
      if (!companyId) {
        throw new Error("Company ID not found");
      }

      const hasExistingTheme = state.currentTheme !== null;
      let response;
      if (hasExistingTheme) {
        response = await axios.put(`/theme/${companyId}`, {
          themePalette: theme.theme,
          themeType: theme.themeType,
          themeMode: theme.themeMode,
        });
      } else {
        response = await axios.post(`/theme`, {
          companyId,
          themePalette: theme.theme,
          themeType: theme.themeType,
          themeMode: theme.themeMode,
        });
      }

      // Show alert only if status is 200 or 201
      if (response && (response.status === 200 || response.status === 201)) {
        Vue.prototype.$message.success("Theme updated successfully!");
      }

      // Update local state
      commit("SET_CURRENT_THEME", theme);

      if (theme) {
        commit("SET_THEME_PALATTE", theme);
        // Apply the colors to :root variables
        const colorMap = {
          "--color-primary-brand": "--primary-color-brand",
          "--color-brand-500": "--brand-500",
          "--color-brand-400": "--brand-400",
          "--color-brand-300": "--brand-300",
          "--color-brand-200": "--brand-200",
          "--color-brand-100": "--brand-100",
          "--color-brand-50": "--brand-50",
          "--page-background": "--page-background",
          "--container-background": "--container-background",
          "--page-border": "--page-border",
          "--card-color": "--card-color",
          "--card-border": "--card-border-color",
          "--filter-border": "--filter-border-color",
          "--color-black": "--color-black",
          "--color-white": "--color-white",
          "--color-black-950": "--black-950",
          "--color-black-900": "--black-900",
          "--color-black-800": "--black-800",
          "--color-black-700": "--black-700",
          "--color-black-600": "--black-600",
          "--color-black-500": "--black-500",
          "--color-black-400": "--black-400",
          "--color-black-300": "--black-300",
          "--color-black-200": "--black-200",
          "--color-black-150": "--black-150",
          "--color-black-100": "--black-100",
          "--color-black-50": "--black-50",
          "--color-hyperlink": "--hyper-links-blue",
          "--color-active": "--active-color",
          "--color-inactive": "--inactive-color",
          "--color-assign": "--green-assign-color",
          "--color-note": "--note-orange-color",
          "--color-ai-gradient": "--ai-button-gradient",
        };
        Object.entries(theme.theme).forEach(([key, value]) => {
          const cssVar = colorMap[key] || key;
          document.documentElement.style.setProperty(cssVar, value);
        });
      }

      // Fetch updated themes to refresh the state
      await dispatch("fetchThemes", companyId);
      return true;
    } catch (error) {
      console.error("Failed to update theme:", error);
      Vue.prototype.$message.error("Failed to update theme. Please try again.");
      throw error;
    }
  },

  async fetchThemes({ commit, rootState }, companyId) {
    try {
      const id = rootState.company.companyDetails?._id || companyId;
      if (!id) {
        throw new Error("Company ID not found");
      }

      // Fetch default themes
      const defaultResponse = await axios.get(`/theme/${id}`);

      if (
        !defaultResponse.data ||
        typeof defaultResponse.data.default !== "object"
      ) {
        throw new Error("Invalid theme data received");
      }

      // Map and validate themes from default object
      const themes = Object.values(defaultResponse.data.default)
        .filter(
          (theme) =>
            theme && (theme.theme_palette || theme.theme_palatte) && theme.color
        )
        .map((theme) => ({
          name: theme.theme_palette || theme.theme_palatte,
          colors: theme.color,
        }));

      // Set available themes from default
      commit("SET_AVAILABLE_THEMES", themes);

      // Fetch custom theme data to get the selected theme
      try {
        const customResponse = await axios.get(`/theme/custom/${id}`);
        // Fix: Access currentTheme correctly (remove .Object)
        const currentTheme =
          customResponse.data?.selected_theme_palatte || null;
        commit("SET_CURRENT_THEME", currentTheme);
        commit("SET_CURRENT_THEME_TYPE", customResponse.data?.theme_type);
        commit("SET_CURRENT_THEME_MODE", customResponse.data?.theme_mode);
        if (currentTheme) {
          commit("SET_THEME_PALATTE", currentTheme);
          const colorMap = {
            "--color-primary-brand": "--primary-color-brand",
            "--color-brand-500": "--brand-500",
            "--color-brand-400": "--brand-400",
            "--color-brand-300": "--brand-300",
            "--color-brand-200": "--brand-200",
            "--color-brand-100": "--brand-100",
            "--color-brand-50": "--brand-50",
            "--page-background": "--page-background",
            "--container-background": "--container-background",
            "--page-border": "--page-border",
            "--card-color": "--card-color",
            "--card-border": "--card-border-color",
            "--filter-border": "--filter-border-color",
            "--color-black": "--colorblack",
            "--color-white": "--color-white",
            "--color-black-950": "--black-950",
            "--color-black-900": "--black-900",
            "--color-black-800": "--black-800",
            "--color-black-700": "--black-700",
            "--color-black-600": "--black-600",
            "--color-black-500": "--black-500",
            "--color-black-400": "--black-400",
            "--color-black-300": "--black-300",
            "--color-black-200": "--black-200",
            "--color-black-150": "--black-150",
            "--color-black-100": "--black-100",
            "--color-black-50": "--black-50",
            "--color-hyperlink": "--hyper-links-blue",
            "--color-active": "--active-color",
            "--color-inactive": "--inactive-color",
            "--color-assign": "--green-assign-color",
            "--color-note": "--note-orange-color",
            "--color-ai-gradient": "--ai-button-gradient",
          };
          Object.entries(currentTheme).forEach(([key, value]) => {
            const cssVar = colorMap[key] || key;
            document.documentElement.style.setProperty(cssVar, value);
          });
        }
      } catch (customError) {
        console.warn("Failed to fetch custom theme data:", customError);
        // Fallback to default response
        const currentTheme = defaultResponse.data.custom?.theme || null;
        commit("SET_CURRENT_THEME", currentTheme);

        // Find and set the theme palette for fallback case
        if (currentTheme) {
          const matchingTheme = themes.find(
            (theme) => theme.name === currentTheme
          );
          if (matchingTheme && matchingTheme.colors) {
            commit("SET_THEME_PALATTE", matchingTheme.colors);
            // Apply the colors to :root variables
            const colorMap = {
              "--color-primary-brand": "--primary-color-brand",
              "--color-brand-500": "--brand-500",
              "--color-brand-400": "--brand-400",
              "--color-brand-300": "--brand-300",
              "--color-brand-200": "--brand-200",
              "--color-brand-100": "--brand-100",
              "--color-brand-50": "--brand-50",
              "--page-background": "--page-background",
              "--container-background": "--container-background",
              "--page-border": "--page-border",
              "--card-color": "--card-color",
              "--card-border": "--card-border-color",
              "--filter-border": "--filter-border-color",
              "--color-black": "--color-black",
              "--color-white": "--color-white",
              "--color-black-950": "--black-950",
              "--color-black-900": "--black-900",
              "--color-black-800": "--black-800",
              "--color-black-700": "--black-700",
              "--color-black-600": "--black-600",
              "--color-black-500": "--black-500",
              "--color-black-400": "--black-400",
              "--color-black-300": "--black-300",
              "--color-black-200": "--black-200",
              "--color-black-150": "--black-150",
              "--color-black-100": "--black-100",
              "--color-black-50": "--black-50",
              "--color-hyperlink": "--hyper-links-blue",
              "--color-active": "--active-color",
              "--color-inactive": "--inactive-color",
              "--color-assign": "--green-assign-color",
              "--color-note": "--note-orange-color",
              "--color-ai-gradient": "--ai-button-gradient",
            };
            Object.entries(matchingTheme.colors).forEach(([key, value]) => {
              const cssVar = colorMap[key] || key;
              document.documentElement.style.setProperty(cssVar, value);
            });
          }
        }
      }

      return themes;
    } catch (error) {
      console.error("Failed to fetch themes:", error);
      Vue.prototype.$message.error(
        "Failed to fetch themes. Please refresh or contact support."
      );
      commit("SET_AVAILABLE_THEMES", []);
      commit("SET_CURRENT_THEME", null);
      const defaultColors = {
        "--color-primary-brand": "#178a7c",
        "--color-brand-500": "#21ab98",
        "--color-brand-400": "#3ac7b1",
        "--color-brand-300": "#69dfca",
        "--color-brand-200": "#a0efde",
        "--color-brand-100": "#d0f7ee",
        "--color-brand-50": "#f1fcf9",
        "--color-black-900": "#2F80ED",
        "--color-black-800": "#2F80ED",
        "--color-black-700": "#2F80ED",
        "--color-black-600": "#2F80ED",
        "--color-black-500": "#2F80ED",
        "--color-black-400": "#2F80ED",
        "--color-black-300": "#2F80ED",
        "--color-black-200": "#2F80ED",
        "--color-black-150": "#2F80ED",
        "--color-black-100": "#2F80ED",
        "--color-black-50": "#2F80ED",
        "--color-hyperlink": "#2F80ED",
        "--color-active": "#2F80ED",
        "--color-inactive": "#2F80ED",
        "--color-assign": "#2F80ED",
        "--color-note": "#2F80ED",
      };
      const colorMap = {
        "--color-primary-brand": "--primary-color-brand",
        "--color-brand-500": "--brand-500",
        "--color-brand-400": "--brand-400",
        "--color-brand-300": "--brand-300",
        "--color-brand-200": "--brand-200",
        "--color-brand-100": "--brand-100",
        "--color-brand-50": "--brand-50",
        "--page-background": "--page-background",
        "--container-background": "--container-background",
        "--page-border": "--page-border",
        "--card-color": "--card-color",
        "--card-border": "--card-border-color",
        "--filter-border": "--filter-border-color",
        "--color-black": "--color-black",
        "--color-white": "--color-white",
        "--color-black-950": "--black-950",
        "--color-black-900": "--black-900",
        "--color-black-800": "--black-800",
        "--color-black-700": "--black-700",
        "--color-black-600": "--black-600",
        "--color-black-500": "--black-500",
        "--color-black-400": "--black-400",
        "--color-black-300": "--black-300",
        "--color-black-200": "--black-200",
        "--color-black-150": "--black-150",
        "--color-black-100": "--black-100",
        "--color-black-50": "--black-50",
        "--color-hyperlink": "--hyper-links-blue",
        "--color-active": "--active-color",
        "--color-inactive": "--inactive-color",
        "--color-assign": "--green-assign-color",
        "--color-note": "--note-orange-color",
        "--color-ai-gradient": "--ai-button-gradient",
      };
      Object.entries(defaultColors).forEach(([key, value]) => {
        const cssVar = colorMap[key] || key;
        document.documentElement.style.setProperty(cssVar, value);
      });
      throw error;
    }
  },
};

const getters = {
  getCurrentTheme: (state) => state.currentTheme,
  getAvailableThemes: (state) => state.availableThemes || [],
  getThemePalatte: (state) => state.theme_palatte,
  getThemeType: (state) => state.themeType,
  getThemeMode: (state) => state.themeMode,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
